import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { ROLE_LABEL, ROLE_BADGE, canManageRoster, effectiveRoles, isOfficer } from "../lib/permissions";
import { Button } from "../components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Calendar, ClipboardCheck, Instagram, Users, Bell, Plus, LogOut, Settings, Palette, Megaphone, Search, Tags } from "lucide-react";
import CalendarView from "../components/CalendarView";
import ApprovalsView from "../components/ApprovalsView";
import SocialPlannerView from "../components/SocialPlannerView";
import RosterView from "../components/RosterView";
import NotificationsView from "../components/NotificationsView";
import CreateEventDialog from "../components/CreateEventDialog";
import AccountDialog from "../components/AccountDialog";
import BrandingStudio from "../components/BrandingStudio";
import CommsView from "../components/CommsView";
import CategoriesView from "../components/CategoriesView";
import LookupView from "../components/LookupView";
import { setCategories } from "../lib/permissions";
import { useBranding, imgStyle } from "../context/BrandingContext";

export default function Dashboard() {
  const { user, logout, impersonating, exitImpersonation } = useAuth();
  const { branding } = useBranding();
  const logo = branding.images.header_logo;
  const [tab, setTab] = useState("calendar");
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [posts, setPosts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [lookupEvent, setLookupEvent] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [term, setTerm] = useState("");

  const refreshAll = useCallback(async () => {
    const [ev, us, pd, po, no, cats] = await Promise.all([
      api.get("/events"), api.get("/users"), api.get("/events/pending"),
      api.get("/posts"), api.get("/notifications"), api.get("/categories"),
    ]);
    setEvents(ev.data); setUsers(us.data); setPending(pd.data);
    setPosts(po.data); setNotifications(no.data);
    setCategories(cats.data);
  }, []);

  useEffect(() => { refreshAll(); }, [refreshAll]);
  useEffect(() => { api.get("/meta/config").then(r => setTerm(r.data.term)).catch(() => {}); }, []);

  const unreadCount = notifications.filter(n => !n.read).length;
  const pendingCount = pending.length;
  const officer = isOfficer(effectiveRoles(user));

  return (
    <div className="min-h-screen hero-grain">
      {/* Header */}
      <header className="sticky top-0 z-40 glass-card !bg-[#080C16]/85 !border-x-0 !border-t-0 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={logo.src} alt="Chapter logo" style={imgStyle(logo, 44)} className="object-contain rounded-sm" />
            <div>
              <div className="font-serif-accent text-lg leading-tight text-[var(--sn-gold)]" data-testid="app-title">Sigma Nu - Nu Epsilon</div>
              <div className="text-[11px] uppercase tracking-widest text-[var(--sn-border)] font-semibold" data-testid="term-pill">Chapter · {term || "…"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1.5 flex-wrap max-w-[340px]" data-testid="header-role-badge">
              {effectiveRoles(user).map(r => (
                <span key={r} data-testid={`header-role-badge-${r}`}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border ${ROLE_BADGE[r]}`}>
                  {ROLE_LABEL[r]}
                </span>
              ))}
            </div>
            <Button
              data-testid="create-event-button"
              onClick={() => setCreateOpen(true)}
              className="bg-[var(--sn-gold)] hover:brightness-95 text-black font-bold gap-2">
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">New Event</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-trigger" variant="ghost" className="text-neutral-200 hover:bg-white/5">
                  {user.name}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-neutral-900 border-neutral-700">
                <DropdownMenuItem disabled className="text-neutral-400">{user.email}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="my-account-button" onClick={() => setAccountOpen(true)} className="text-neutral-200 focus:bg-white/5">
                  <Settings className="w-4 h-4 mr-2" /> My Account
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-rose-300 focus:bg-rose-950">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {impersonating && (
          <div data-testid="impersonation-banner"
            className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--sn-gold)]/50 bg-[var(--sn-gold)]/10 px-4 py-3">
            <span className="text-sm text-neutral-200">
              Viewing the app as <b className="text-[var(--sn-gold)]">{user.name}</b> ({ROLE_LABEL[user.role]})
            </span>
            <Button data-testid="exit-impersonation-button" onClick={exitImpersonation} size="sm"
              className="bg-[var(--sn-gold)] hover:brightness-95 text-black font-bold">
              Return to Admin
            </Button>
          </div>
        )}
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-slate-900/60 border border-white/5 mb-6 flex-wrap h-auto p-1">
            <TabsTrigger data-testid="nav-tab-calendar" value="calendar" className="gap-2 data-[state=active]:bg-[var(--sn-gold)] data-[state=active]:text-black">
              <Calendar className="w-4 h-4" /> Calendar
            </TabsTrigger>
            <TabsTrigger data-testid="nav-tab-lookup" value="lookup" className="gap-2 data-[state=active]:bg-[var(--sn-gold)] data-[state=active]:text-black">
              <Search className="w-4 h-4" /> Lookup
            </TabsTrigger>
            <TabsTrigger data-testid="nav-tab-approvals" value="approvals" className="gap-2 data-[state=active]:bg-[var(--sn-gold)] data-[state=active]:text-black">
              <ClipboardCheck className="w-4 h-4" /> Approvals
              {pendingCount > 0 && <span className="ml-1 bg-rose-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">{pendingCount}</span>}
            </TabsTrigger>
            <TabsTrigger data-testid="nav-tab-social-planner" value="planner" className="gap-2 data-[state=active]:bg-[var(--sn-gold)] data-[state=active]:text-black">
              <Instagram className="w-4 h-4" /> Social Planner
            </TabsTrigger>
            <TabsTrigger data-testid="nav-tab-roster" value="roster" className="gap-2 data-[state=active]:bg-[var(--sn-gold)] data-[state=active]:text-black">
              <Users className="w-4 h-4" /> Roster
            </TabsTrigger>
            <TabsTrigger data-testid="nav-tab-notifications" value="notifications" className="gap-2 data-[state=active]:bg-[var(--sn-gold)] data-[state=active]:text-black">
              <Bell className="w-4 h-4" /> Notifications
              {unreadCount > 0 && <span className="ml-1 bg-[var(--sn-gold)] text-black text-[10px] px-1.5 py-0.5 rounded-full font-semibold">{unreadCount}</span>}
            </TabsTrigger>
            {officer && (
              <TabsTrigger data-testid="nav-tab-comms" value="comms" className="gap-2 data-[state=active]:bg-[var(--sn-gold)] data-[state=active]:text-black">
                <Megaphone className="w-4 h-4" /> Messages
              </TabsTrigger>
            )}
            {user.role === "admin" && (
              <TabsTrigger data-testid="nav-tab-categories" value="categories" className="gap-2 data-[state=active]:bg-[var(--sn-gold)] data-[state=active]:text-black">
                <Tags className="w-4 h-4" /> Categories
              </TabsTrigger>
            )}
            {user.role === "admin" && (
              <TabsTrigger data-testid="nav-tab-branding" value="branding" className="gap-2 data-[state=active]:bg-[var(--sn-gold)] data-[state=active]:text-black">
                <Palette className="w-4 h-4" /> Branding
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="calendar"><CalendarView events={events} onRefresh={refreshAll} user={user}
            onEdit={(ev) => { setEditingEvent(ev); setCreateOpen(true); }}
            externalSelected={lookupEvent} onExternalClose={() => setLookupEvent(null)} /></TabsContent>
          <TabsContent value="lookup"><LookupView events={events} onSelect={(ev) => { setLookupEvent(ev); setTab("calendar"); }} /></TabsContent>
          <TabsContent value="approvals"><ApprovalsView pending={pending} user={user} onRefresh={refreshAll} /></TabsContent>
          <TabsContent value="planner"><SocialPlannerView posts={posts} events={events} onRefresh={refreshAll} /></TabsContent>
          <TabsContent value="roster">
            <RosterView users={users} me={user} canManage={canManageRoster(effectiveRoles(user))} onRefresh={refreshAll} />
          </TabsContent>
          <TabsContent value="notifications"><NotificationsView notifications={notifications} onRefresh={refreshAll} /></TabsContent>
          {officer && (
            <TabsContent value="comms"><CommsView events={events} users={users} me={user} /></TabsContent>
          )}
          {user.role === "admin" && (
            <TabsContent value="categories"><CategoriesView onRefresh={refreshAll} /></TabsContent>
          )}
          {user.role === "admin" && (
            <TabsContent value="branding"><BrandingStudio /></TabsContent>
          )}
        </Tabs>
      </main>

      <CreateEventDialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setEditingEvent(null); }}
        user={user} users={users} event={editingEvent} onCreated={refreshAll} />
      <AccountDialog open={accountOpen} onOpenChange={setAccountOpen} />
    </div>
  );
}
